import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | authed | anon

  const refresh = useCallback(async () => {
    try {
      const me = await api('/auth/me');
      setUser(me);
      setStatus('authed');
    } catch {
      setUser(null);
      setStatus('anon');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    function onUnauth() { setUser(null); setStatus('anon'); }
    window.addEventListener('fd-unauthorized', onUnauth);
    return () => window.removeEventListener('fd-unauthorized', onUnauth);
  }, []);

  const login = useCallback(async (email, password) => {
    await api('/auth/login', { method: 'POST', body: { email, password } });
    await refresh(); // pull full profile incl. permissions
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api('/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
    setUser(null);
    setStatus('anon');
  }, []);

  const can = useCallback(
    (gate) => !!user && (user.role === 'admin' || (user.permissions || []).includes(gate)),
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, status, login, logout, refresh, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
